"use client";

import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { X, Plus, Minus } from "lucide-react";
import { PALETTE } from "../lib/colorPallete";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { calculatePrice } from "../lib/pricingService";
import { CartItemProps } from "../types/types";
import Image from "next/image";

const CartItem: React.FC<CartItemProps> = ({ item, onEdit }) => {
  const { updateQuantity, removeFromCart, isLoading } = useCart();
  const { isWholesaleUser } = useAuth();

  const displayImage = useMemo(() => {
    for (const optValue of item.selectedOptions) {
      const optionData = item.product.customization
        ?.flatMap((c) => c.options)
        .find((o) => o.value === optValue);

      if (
        optionData?.customizationSpecificImageUrls &&
        optionData.customizationSpecificImageUrls.length > 0
      ) {
        return optionData.customizationSpecificImageUrls[0];
      }
    }

    const selectedColorName = item.selectedOptions.find(
      (opt) =>
        Array.isArray(item.product.colors) &&
        item.product.colors.some((color) => color.name === opt),
    );

    if (selectedColorName) {
      const colorData = item.product.colors?.find(
        (c) => c.name === selectedColorName,
      );

      if (colorData) {
        if (colorData.variantImages && colorData.variantImages.length > 0) {
          const otherOptions = item.selectedOptions.filter(
            (opt) => opt !== selectedColorName,
          );

          const matchingVariant = colorData.variantImages.find((variant) => {
            return variant.requiredOptions.every((reqOpt) =>
              otherOptions.some(
                (selectedOpt) =>
                  selectedOpt.toLowerCase().includes(reqOpt.toLowerCase()) ||
                  reqOpt.toLowerCase().includes(selectedOpt.toLowerCase()),
              ),
            );
          });

          if (matchingVariant && matchingVariant.imageUrls.length > 0) {
            return matchingVariant.imageUrls[0];
          }
        }

        if (colorData.imageUrls && colorData.imageUrls.length > 0) {
          return colorData.imageUrls[0];
        }
      }
    }
    if (
      item.product.commonImageUrls &&
      item.product.commonImageUrls.length > 0
    ) {
      return item.product.commonImageUrls[0];
    }
    return "/placeholder.png";
  }, [item]);

  const itemSubtotal = useMemo(() => {
    const priceBreakdown = calculatePrice(
      item.product,
      item.selectedOptions,
      isWholesaleUser,
    );

    return priceBreakdown.total * item.quantity;
  }, [item, isWholesaleUser]);

  const handleUpdateQuantity = (e: React.MouseEvent, newQuantity: number) => {
    e.stopPropagation();
    updateQuantity(item._id, newQuantity);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeFromCart(item._id);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -50, transition: { duration: 0.2 } }}
      onClick={() => onEdit(item)}
      className="flex items-start gap-4 py-4 cursor-pointer hover:bg-stone-100 transition-colors rounded-lg px-2 -mx-2"
      style={{ backgroundColor: `${PALETTE.midGreen}10` }}
    >
      <div
        className="relative w-24 h-24 shrink-0 rounded-md p-1"
        style={{ backgroundColor: "white" }}
      >
        <Image
          src={displayImage}
          alt={item.product.modelName}
          objectFit="contain"
          fill
        />
      </div>

      <div className="flex-grow">
        <h4 className="font-semibold" style={{ color: PALETTE.darkGreen }}>
          {item.product.modelName}
        </h4>
        <div
          className="text-xs mt-1 space-y-0.5"
          style={{ color: `${PALETTE.darkGreen}B3` }}
        >
          {item.selectedOptions
            .filter((opt) => {
              const isColor = item.product.colors.some((c) => c.name === opt);
              return opt !== "Standard" && !isColor;
            })
            .map((opt) => (
              <p key={opt}>{opt.split(" (for")[0]}</p>
            ))}
        </div>

        <div className="flex items-center gap-2 mt-3">
          <button
            disabled={isLoading}
            onClick={(e) => handleUpdateQuantity(e, item.quantity - 1)}
            className="p-1.5 rounded-full border transition-colors disabled:opacity-50"
            style={{
              borderColor: `${PALETTE.midGreen}40`,
              color: PALETTE.darkGreen,
              backgroundColor: "white",
            }}
          >
            <Minus size={14} />
          </button>
          <span
            className="font-semibold w-6 text-center"
            style={{ color: PALETTE.darkerGreen }}
          >
            {item.quantity}
          </span>
          <button
            disabled={isLoading}
            onClick={(e) => handleUpdateQuantity(e, item.quantity + 1)}
            className="p-1.5 rounded-full border transition-colors disabled:opacity-50"
            style={{
              borderColor: `${PALETTE.midGreen}40`,
              color: PALETTE.darkGreen,
              backgroundColor: "white",
            }}
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      <div className="text-right flex flex-col items-end h-[96px]">
        <p className="font-semibold" style={{ color: PALETTE.darkGreen }}>
          {new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 0,
          }).format(itemSubtotal)}
        </p>
        <button
          disabled={isLoading}
          onClick={handleRemove}
          className="mt-auto p-1 rounded-full transition-colors disabled:opacity-50"
          style={{
            color: `${PALETTE.darkGreen}80`,
            backgroundColor: "transparent",
          }}
          title="Remove item"
        >
          <X size={16} />
        </button>
      </div>
    </motion.div>
  );
};

export default CartItem;
